import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';
import { first } from 'rxjs/operators';
import { JWTToken } from '../models/authentication';
import { AuthenticationService, StorageService } from '../services';

/**
 * Adds the authorization header if the token exists in local storage.
 */
@UntilDestroy()
@Injectable()
export class JwtInterceptor implements HttpInterceptor, OnDestroy {

  private passedTime: number = 900000; // 15 minutes: 15 min * 60 sec * 1000 miliseconds
  private isRefreshToken: boolean = false;
  private authenticatedUser: JWTToken;

  constructor(private authService: AuthenticationService, private storage: StorageService) {
    this.storage.castToken
      .pipe(untilDestroyed(this))
      .subscribe((token: JWTToken) => this.authenticatedUser = token);
  }

  public ngOnDestroy(): void { }

  /**
   * Adds the authorization header if the token exists in local storage.
   */
  public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (request.responseType === 'text') {
      return next.handle(request);
    }

    const currentUser = this.authenticatedUser;

    if (currentUser &&
      currentUser.issuedAt != null &&
      currentUser.expireAt != null &&
      currentUser.refreshToken != null &&
      this.isAbleToGetRefreshToken(currentUser.issuedAt, currentUser.expireAt)
    ) {
      if (!this.isRefreshToken) {
        this.isRefreshToken = true;
        this.authService.refresh(currentUser.refreshToken)
          .pipe(first())
          .subscribe({
            next: (data: JWTToken) => {
              request = request.clone({
                setHeaders: {
                  Authorization: `Bearer ${data.token}`
                }
              });
              return next.handle(request);
            },
            error: e => {
              next.handle(request);
            }
          });
      } else {
        this.isRefreshToken = false;
      }
    }

    if (currentUser != null && currentUser.token != null) {
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${currentUser.token}`
        }
      });
    }

    return next.handle(request);
  }

  /**
   * Checks if the enough time has passed since token was issued at.
   */
  private isAbleToGetRefreshToken(issuedAt: number, expireAt: number): boolean {
    const thousandMilliseconds: number = 1000;
    const issuedAtTimestamp: number = issuedAt * thousandMilliseconds;
    const expireAtTimestamp: number = expireAt * thousandMilliseconds;
    const now: number = +new Date();

    return now - issuedAtTimestamp > this.passedTime && now < expireAtTimestamp;
  }
}
